// Planning v2 — parallel module to /planning. Mounted at /planning-v2.
// Five tabs: Dashboard · Departments · Workers · Workload · Shifts.
// Tenant-scoped on the server; this UI just renders what the backend
// returns. Write-permission rules:
//   • Smeding (legacy tenant): master-admin only.
//   • Other tenants: planning_v2_admin flag.
import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { CalendarDays, Building2, Users, Boxes, Layers, Loader2, Sparkles, CalendarRange, ClipboardCheck, BarChart3, Plane, MessageSquareWarning, Download, Gauge, Coins, Network, KeyRound, ClipboardList, Scale } from 'lucide-react';

const DashboardTab    = lazy(() => import('./planning_v2/DashboardTab').then(m => ({ default: m.DashboardTab })));
const DepartmentsTab  = lazy(() => import('./planning_v2/DepartmentsTab').then(m => ({ default: m.DepartmentsTab })));
const WorkersTab      = lazy(() => import('./planning_v2/WorkersTab').then(m => ({ default: m.WorkersTab })));
const WorkloadTab     = lazy(() => import('./planning_v2/WorkloadTab').then(m => ({ default: m.WorkloadTab })));
const ShiftsTab       = lazy(() => import('./planning_v2/ShiftsTab').then(m => ({ default: m.ShiftsTab })));
const CalendarTab     = lazy(() => import('./planning_v2/CalendarTab').then(m => ({ default: m.CalendarTab })));
const PresenceTab     = lazy(() => import('./planning_v2/PresenceTab').then(m => ({ default: m.PresenceTab })));
const PlanVsActualTab = lazy(() => import('./planning_v2/PlanVsActualTab').then(m => ({ default: m.PlanVsActualTab })));
const HolidaysTab     = lazy(() => import('./planning_v2/HolidaysTab').then(m => ({ default: m.HolidaysTab })));
const BehaviourTab    = lazy(() => import('./planning_v2/BehaviourTab').then(m => ({ default: m.BehaviourTab })));
const ExportTab       = lazy(() => import('./planning_v2/ExportTab').then(m => ({ default: m.ExportTab })));
const EfficiencyTab   = lazy(() => import('./planning_v2/EfficiencyTab').then(m => ({ default: m.EfficiencyTab })));
const PaymentBucketsTab = lazy(() => import('./planning_v2/PaymentBucketsTab').then(m => ({ default: m.PaymentBucketsTab })));
const TenantAdminTab  = lazy(() => import('./planning_v2/TenantAdminTab').then(m => ({ default: m.TenantAdminTab })));
const AccessTab       = lazy(() => import('./planning_v2/AccessTab').then(m => ({ default: m.AccessTab })));
const HandoverTab     = lazy(() => import('./planning_v2/HandoverTab').then(m => ({ default: m.HandoverTab })));
const BalanceTab      = lazy(() => import('./planning_v2/BalanceTab').then(m => ({ default: m.BalanceTab })));

const TabFallback = () => (
  <div className="flex items-center justify-center py-16 text-sm text-muted-foreground" data-testid="pv2-tab-fallback">
    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
  </div>
);

const todayIso = () => new Date().toISOString().slice(0, 10);

export const PlanningV2Page = () => {

// ── Small ISO-week jumper that sits next to the day-picker on the
// Planning v2 header. Pure helper component; resolves a (Year, Week)
// pair to Monday's date and pushes it back to the parent so every
// tab (Dashboard included) stays in sync.
const _isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const _isoWeekOf = (dStr) => {
  const d = new Date(dStr + 'T00:00:00');
  const t = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dn = ((t.getDay() + 6) % 7) + 1;
  t.setDate(t.getDate() + 4 - dn);
  const ys = new Date(t.getFullYear(), 0, 1);
  return { week: Math.ceil(((t - ys) / 86400000 + 1) / 7), year: t.getFullYear() };
};
const _isoWeeksInYear = (y) => _isoWeekOf(_isoLocal(new Date(y, 11, 28))).week;
const _mondayOfISOWeek = (year, week) => {
  const j = new Date(year, 0, 4);
  const jm = new Date(j); jm.setDate(j.getDate() - ((j.getDay() + 6) % 7));
  const m = new Date(jm); m.setDate(jm.getDate() + (week - 1) * 7);
  return _isoLocal(m);
};
function WeekJumper({ date, onJump }) {
  const iw = _isoWeekOf(date);
  const wmax = _isoWeeksInYear(iw.year);
  const step = (delta) => {
    let w = iw.week + delta, y = iw.year;
    if (w < 1) { y -= 1; w = _isoWeeksInYear(y); }
    else if (w > _isoWeeksInYear(y)) { y += 1; w = 1; }
    onJump(_mondayOfISOWeek(y, w));
  };
  return (
    <span className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1" data-testid="pv2-weekjumper">
      <button type="button" onClick={() => step(-1)} className="p-1 rounded hover:bg-muted" aria-label="Previous week" data-testid="pv2-weekjumper-prev">‹</button>
      <span className="text-xs text-muted-foreground">Week</span>
      <input
        type="number" min="1" max={wmax} value={iw.week}
        onChange={(e) => {
          const v = Math.max(1, Math.min(wmax, parseInt(e.target.value || '0', 10) || 1));
          onJump(_mondayOfISOWeek(iw.year, v));
        }}
        className="w-12 h-7 text-center tabular-nums rounded border bg-background text-sm"
        data-testid="pv2-weekjumper-input"
      />
      <span className="text-xs text-muted-foreground tabular-nums">{iw.year}</span>
      <button type="button" onClick={() => step(1)} className="p-1 rounded hover:bg-muted" aria-label="Next week" data-testid="pv2-weekjumper-next">›</button>
    </span>
  );
}

  const { user } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [date, setDate] = useState(todayIso());
  const [myAccess, setMyAccess] = useState({ global_editor: false, modules: [] });

  useEffect(() => {
    let cancelled = false;
    import('./planning_v2/api').then(({ myModuleAccess }) =>
      myModuleAccess().then((r) => { if (!cancelled) setMyAccess(r || { global_editor: false, modules: [] }); }).catch(() => {})
    );
    return () => { cancelled = true; };
  }, []);

  // Global editor mirrors backend `_can_write` — Master Admin only.
  const roleEditor = useMemo(() => !!(user && (user.is_master_admin || user.role === 'master_admin')), [user]);

  const globalEditor = roleEditor || myAccess.global_editor;
  // Any Admin / Master Admin account can edit Overdracht (shift handovers),
  // regardless of granular planning-module grants.
  const isAdmin = useMemo(() => !!(user && (user.is_master_admin || ['admin', 'master_admin'].includes(user.role))), [user]);
  // Per-module write check: global editors edit everything; otherwise a
  // user may have been granted specific modules via the Access tab.
  const can = useCallback(
    (mod) => globalEditor || (myAccess.modules || []).includes(mod),
    [globalEditor, myAccess]
  );
  const editCount = (myAccess.modules || []).length;
  const hasAnyEdit = globalEditor || editCount > 0;
  const isPartialEdit = !globalEditor && editCount > 0 && editCount < (myAccess.total_modules || 8);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="planning-v2-page">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <CalendarDays className="h-5 w-5 text-primary" />
              <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10">FlowCrew&trade;</Badge>
            </div>
            <h1 className="font-display text-2xl tracking-tight">Workforce Planning</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Multi-tenant warehouse planning — workload-driven hours, shift assignments, AI-assisted suggestions.
              <span className="ml-2 inline-flex items-center gap-1 text-xs">
                <span className="text-muted-foreground/70">Tenant:</span>
                <strong>{user?.company || '—'}</strong>
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-muted-foreground" htmlFor="pv2-date">Date</label>
            <input
              id="pv2-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="pv2-date-picker"
            />
            <WeekJumper date={date} onJump={setDate} />
            {!hasAnyEdit && (
              <Badge variant="outline" className="text-xs" data-testid="pv2-read-only-badge">Read-only access</Badge>
            )}
            {hasAnyEdit && !globalEditor && isPartialEdit && (
              <Badge variant="outline" className="text-xs border-amber-500/40 text-amber-600" data-testid="pv2-partial-edit-badge">Limited edit</Badge>
            )}
          </div>
        </header>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="flex flex-wrap h-auto w-full">
            <TabsTrigger value="dashboard"   data-testid="pv2-tab-dashboard"><CalendarDays className="h-4 w-4 mr-2" />Dashboard</TabsTrigger>
            <TabsTrigger value="calendar"    data-testid="pv2-tab-calendar"><CalendarRange className="h-4 w-4 mr-2" />Calendar</TabsTrigger>
            <TabsTrigger value="departments" data-testid="pv2-tab-departments"><Building2 className="h-4 w-4 mr-2" />Departments</TabsTrigger>
            <TabsTrigger value="workers"     data-testid="pv2-tab-workers"><Users className="h-4 w-4 mr-2" />Workers</TabsTrigger>
            <TabsTrigger value="workload"    data-testid="pv2-tab-workload"><Boxes className="h-4 w-4 mr-2" />Workload</TabsTrigger>
            <TabsTrigger value="shifts"      data-testid="pv2-tab-shifts"><Layers className="h-4 w-4 mr-2" />Shifts</TabsTrigger>
            <TabsTrigger value="presence"    data-testid="pv2-tab-presence"><ClipboardCheck className="h-4 w-4 mr-2" />Presence</TabsTrigger>
            <TabsTrigger value="pvsa"        data-testid="pv2-tab-pvsa"><BarChart3 className="h-4 w-4 mr-2" />Plan vs Actual</TabsTrigger>
            <TabsTrigger value="holidays"    data-testid="pv2-tab-holidays"><Plane className="h-4 w-4 mr-2" />Holidays</TabsTrigger>
            <TabsTrigger value="behaviour"   data-testid="pv2-tab-behaviour"><MessageSquareWarning className="h-4 w-4 mr-2" />Behaviour</TabsTrigger>
            <TabsTrigger value="handover"    data-testid="pv2-tab-handover"><ClipboardList className="h-4 w-4 mr-2" />Overdracht</TabsTrigger>
            <TabsTrigger value="efficiency"  data-testid="pv2-tab-efficiency"><Gauge className="h-4 w-4 mr-2" />Efficiency</TabsTrigger>
            <TabsTrigger value="balance"     data-testid="pv2-tab-balance"><Scale className="h-4 w-4 mr-2" />Balance</TabsTrigger>
            <TabsTrigger value="export"      data-testid="pv2-tab-export"><Download className="h-4 w-4 mr-2" />Export</TabsTrigger>
            <TabsTrigger value="paybuckets"  data-testid="pv2-tab-paybuckets"><Coins className="h-4 w-4 mr-2" />Pay buckets</TabsTrigger>
            {user?.is_master_admin && (
              <TabsTrigger value="tenant" data-testid="pv2-tab-tenant"><Network className="h-4 w-4 mr-2" />Tenant</TabsTrigger>
            )}
            {user?.is_master_admin && (
              <TabsTrigger value="access" data-testid="pv2-tab-access"><KeyRound className="h-4 w-4 mr-2" />Access</TabsTrigger>
            )}
          </TabsList>

          <Suspense fallback={<TabFallback />}>
            <TabsContent value="dashboard"   className="mt-6"><DashboardTab    date={date} canWrite={can('shifts')} onDateChange={setDate} /></TabsContent>
            <TabsContent value="calendar"    className="mt-6"><CalendarTab     date={date} onDateChange={setDate} canWrite={can('shifts')} canEditWorker={can('workers')} /></TabsContent>
            <TabsContent value="departments" className="mt-6"><DepartmentsTab  canWrite={can('departments')} /></TabsContent>
            <TabsContent value="workers"     className="mt-6"><WorkersTab      canWrite={can('workers')} /></TabsContent>
            <TabsContent value="workload"    className="mt-6"><WorkloadTab     date={date} canWrite={can('workload')} /></TabsContent>
            <TabsContent value="shifts"      className="mt-6"><ShiftsTab       date={date} canWrite={can('shifts')} /></TabsContent>
            <TabsContent value="presence"    className="mt-6"><PresenceTab     date={date} canWrite={can('presence')} /></TabsContent>
            <TabsContent value="pvsa"        className="mt-6"><PlanVsActualTab date={date} /></TabsContent>
            <TabsContent value="holidays"    className="mt-6"><HolidaysTab     canWrite={can('holidays')} /></TabsContent>
            <TabsContent value="behaviour"   className="mt-6"><BehaviourTab    /></TabsContent>
            <TabsContent value="handover"    className="mt-6"><HandoverTab     canWrite={can('handover') || isAdmin} /></TabsContent>
            <TabsContent value="efficiency"  className="mt-6"><EfficiencyTab   date={date} /></TabsContent>
            <TabsContent value="balance"     className="mt-6"><BalanceTab      date={date} /></TabsContent>
            <TabsContent value="export"      className="mt-6"><ExportTab       /></TabsContent>
            <TabsContent value="paybuckets"  className="mt-6"><PaymentBucketsTab canWrite={can('paybuckets')} /></TabsContent>
            {user?.is_master_admin && (
              <TabsContent value="tenant" className="mt-6"><TenantAdminTab /></TabsContent>
            )}
            {user?.is_master_admin && (
              <TabsContent value="access" className="mt-6"><AccessTab /></TabsContent>
            )}
          </Suspense>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default PlanningV2Page;
